import { ReactNode, forwardRef } from 'react';
import classNames from 'classnames';

export type TextAreaVariant =
  | 'default'
  | 'outlined'
  | 'filled'
  | 'ghost'
  | 'error'
  | 'success';
export type TextAreaSize = 'sm' | 'md' | 'lg';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: TextAreaVariant;
  size?: TextAreaSize;
  label?: string;
  helperText?: string;
  errorMessage?: string;
  successMessage?: string;
  showCharacterCount?: boolean;
  maxLength?: number;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  className?: string;
  wrapperClassName?: string;
  rows?: number;
}

const sizeStyles: Record<
  TextAreaSize,
  { text: string; padding: string; label: string; helper: string }
> = {
  sm: {
    text: '~text-xs/sm',
    padding: '~px-2/3 ~py-1.5/2',
    label: '~text-xs/sm',
    helper: '~text-[0.625rem]/xs'
  },
  md: {
    text: '~text-sm/base',
    padding: '~px-3/4 ~py-2/2.5',
    label: '~text-sm/base',
    helper: '~text-xs/sm'
  },
  lg: {
    text: '~text-base/lg',
    padding: '~px-4/5 ~py-2.5/3',
    label: '~text-base/lg',
    helper: '~text-sm/base'
  }
};

const variantStyles: Record<
  TextAreaVariant,
  { base: string; focus: string; disabled: string }
> = {
  default: {
    base: 'bg-white dark:bg-secondary-dark-200 border border-gray-300 dark:border-secondary-dark-500 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:border-primary-dark-500 dark:focus:ring-primary-dark-500/50',
    disabled:
      'disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-secondary-dark-100 dark:disabled:text-secondary-dark-600'
  },
  outlined: {
    base: 'bg-transparent border-2 border-gray-300 dark:border-secondary-dark-500 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:border-primary-dark-500',
    disabled:
      'disabled:bg-transparent disabled:text-gray-500 dark:disabled:text-secondary-dark-600'
  },
  filled: {
    base: 'bg-gray-50 dark:bg-secondary-dark-300 border border-gray-200 dark:border-secondary-dark-500 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:bg-secondary-dark-300 dark:focus:border-primary-dark-500',
    disabled:
      'disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-secondary-dark-100 dark:disabled:text-secondary-dark-600'
  },
  ghost: {
    base: 'bg-transparent border-0 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 focus:bg-gray-50 dark:focus:bg-secondary-dark-200',
    disabled: 'disabled:text-gray-500 dark:disabled:text-secondary-dark-600'
  },
  error: {
    base: 'bg-white dark:bg-secondary-dark-200 border-2 border-red-500 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:border-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-1',
    disabled:
      'disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-secondary-dark-100 dark:disabled:text-secondary-dark-600'
  },
  success: {
    base: 'bg-white dark:bg-secondary-dark-200 border-2 border-green-500 text-gray-900 dark:text-secondary-dark-900',
    focus:
      'focus:border-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-1',
    disabled:
      'disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-secondary-dark-100 dark:disabled:text-secondary-dark-600'
  }
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      variant = 'default',
      size = 'md',
      label,
      helperText,
      errorMessage,
      successMessage,
      showCharacterCount = false,
      maxLength,
      leftIcon,
      rightIcon,
      fullWidth = true,
      resize = 'vertical',
      className = '',
      wrapperClassName = '',
      disabled,
      value,
      rows,
      ...props
    },
    ref
  ) => {
    const sizeStyle = sizeStyles[size];
    const currentLength = typeof value === 'string' ? value.length : 0;
    const hasError = Boolean(errorMessage);
    const hasSuccess = Boolean(successMessage) && !hasError;

    let effectiveVariant: TextAreaVariant = variant;
    if (hasError) {
      effectiveVariant = 'error';
    } else if (hasSuccess) {
      effectiveVariant = 'success';
    }

    const effectiveVariantStyle = variantStyles[effectiveVariant];

    const textAreaClasses = classNames(
      'rounded-lg outline-none transition-all duration-200 scrollbar-thin-custom',
      'placeholder:text-gray-400 dark:placeholder:text-secondary-dark-600',
      sizeStyle.text,
      sizeStyle.padding,
      effectiveVariantStyle.base,
      !disabled && effectiveVariantStyle.focus,
      effectiveVariantStyle.disabled,
      resize === 'none' && 'resize-none',
      resize === 'both' && 'resize',
      resize === 'horizontal' && 'resize-x',
      resize === 'vertical' && 'resize-y',
      leftIcon && 'pl-10',
      rightIcon && 'pr-10',
      fullWidth ? 'w-full' : 'w-auto',
      className
    );

    const labelClasses = classNames(
      'block font-medium mb-1.5',
      sizeStyle.label,
      hasError && 'text-red-600 dark:text-red-400',
      hasSuccess && 'text-green-600 dark:text-green-400',
      !hasError && !hasSuccess && 'text-gray-700 dark:text-secondary-dark-800'
    );

    const helperClasses = classNames(
      'mt-1.5',
      sizeStyle.helper,
      hasError && 'text-red-600 dark:text-red-400',
      hasSuccess && 'text-green-600 dark:text-green-400',
      !hasError && !hasSuccess && 'text-gray-500 dark:text-secondary-dark-700'
    );

    return (
      <div
        className={classNames(
          'flex flex-col',
          fullWidth ? 'w-full' : 'w-auto',
          wrapperClassName
        )}>
        {label && (
          <label htmlFor={props.id} className={labelClasses}>
            {label}
            {props.required && <span className='text-red-600 ml-1'>*</span>}
          </label>
        )}

        <div className='relative'>
          {leftIcon && (
            <div className='absolute left-3 top-3 flex items-center text-gray-400 dark:text-secondary-dark-600 pointer-events-none'>
              {leftIcon}
            </div>
          )}

          <textarea
            ref={ref}
            value={value}
            disabled={disabled}
            maxLength={maxLength}
            rows={rows}
            className={textAreaClasses}
            aria-invalid={hasError}
            aria-describedby={
              errorMessage || successMessage || helperText || showCharacterCount
                ? `${props.id || 'textarea'}-description`
                : undefined
            }
            {...props}
          />

          {rightIcon && (
            <div className='absolute right-3 top-3 flex items-center text-gray-400 dark:text-secondary-dark-600 pointer-events-none'>
              {rightIcon}
            </div>
          )}
        </div>

        {(errorMessage ||
          successMessage ||
          helperText ||
          showCharacterCount) && (
          <div
            id={`${props.id || 'textarea'}-description`}
            className='flex items-center justify-between mt-1.5'>
            <div className={helperClasses}>
              {errorMessage && <span>{errorMessage}</span>}
              {!errorMessage && successMessage && <span>{successMessage}</span>}
              {!errorMessage && !successMessage && helperText && (
                <span>{helperText}</span>
              )}
            </div>

            {showCharacterCount && maxLength && (
              <span
                className={classNames(
                  sizeStyle.helper,
                  currentLength > maxLength * 0.9
                    ? 'text-orange-500 dark:text-orange-400'
                    : 'text-gray-500 dark:text-secondary-dark-700'
                )}>
                {currentLength}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

// Named exports for variants
export const TextAreaDefault = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='default' />);
TextAreaDefault.displayName = 'TextAreaDefault';

export const TextAreaOutlined = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='outlined' />);
TextAreaOutlined.displayName = 'TextAreaOutlined';

export const TextAreaFilled = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='filled' />);
TextAreaFilled.displayName = 'TextAreaFilled';

export const TextAreaGhost = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='ghost' />);
TextAreaGhost.displayName = 'TextAreaGhost';

export const TextAreaError = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='error' />);
TextAreaError.displayName = 'TextAreaError';

export const TextAreaSuccess = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'variant'>
>((props, ref) => <TextArea {...props} ref={ref} variant='success' />);
TextAreaSuccess.displayName = 'TextAreaSuccess';

// Named exports for sizes
export const TextAreaSm = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'size'>
>((props, ref) => <TextArea {...props} ref={ref} size='sm' />);
TextAreaSm.displayName = 'TextAreaSm';

export const TextAreaMd = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'size'>
>((props, ref) => <TextArea {...props} ref={ref} size='md' />);
TextAreaMd.displayName = 'TextAreaMd';

export const TextAreaLg = forwardRef<
  HTMLTextAreaElement,
  Omit<TextAreaProps, 'size'>
>((props, ref) => <TextArea {...props} ref={ref} size='lg' />);
TextAreaLg.displayName = 'TextAreaLg';

export default TextArea;
